import React, { forwardRef } from 'react';

import { Box, Flex, Grid } from '@chakra-ui/react';
import { Button, EmployeeIdBadge } from '@mesh.ai/rabbit';
import { SidePanelButton, SIDE_PANEL_BUTTON_SIZE } from 'components/RabbitOKRSidePanel/Components/SidePanelButton';
import ScrollableSectionNavV2 from 'components/RabbitOKRSidePanel/ScrollableSectionNav/ScrollableSectionNavV2';

import { Details2 } from 'assets/assetsV1/iconComponents/Details2';
import { PerformanceHistory } from 'assets/assetsV1/iconComponents/PerformanceHistory';
import { ProposedPayroll } from 'assets/assetsV1/iconComponents/ProposedPayroll';

import { BottomDrawer, DrawerContent } from 'uiToolkitV1/BottomDrawer';
import { DrawerBody } from 'uiToolkitV1/BottomDrawer/DrawerBody';
import { DrawerHeader } from 'uiToolkitV1/BottomDrawer/DrawerHeader';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { CompensationDetails } from '../CompensationRecommendation/components/CompensationDetails';
import { CompensationEmployeeDetails } from '../CompensationRecommendation/components/CompensationEmployeeDetails';
import { CompensationHistory } from '../CompensationRecommendation/components/CompensationHistory';
import { CompensationOverview } from '../CompensationRecommendation/components/CompensationOverview';

const SectionContentContainer = ({ children }: { children: React.ReactNode }) => (
  <Box w="100%" borderRadius="8px" bg="white">
    {children}
  </Box>
);

const DetailsSection = forwardRef<HTMLDivElement>((props, ref) => (
  <SectionContentContainer>
    <div ref={ref}>
      <CompensationDetails headerText="Proposed Payroll" isBottomSheet />
    </div>
  </SectionContentContainer>
));

const OverviewSection = forwardRef<HTMLDivElement>((props, ref) => (
  <SectionContentContainer>
    <div ref={ref}>
      <CompensationOverview />
    </div>
  </SectionContentContainer>
));

const HistorySection = forwardRef<HTMLDivElement>((props, ref) => (
  <SectionContentContainer>
    <div ref={ref}>
      <CompensationHistory />
    </div>
  </SectionContentContainer>
));

const EmployeeDetailsSection = forwardRef<HTMLDivElement>((props, ref) => (
  <SectionContentContainer>
    <div ref={ref}>
      <CompensationEmployeeDetails isBottomSheet />
    </div>
  </SectionContentContainer>
));

const EmployeeDetailsIcon = () => <EmployeeIdBadge height={24} width={24} />;

export const CompensationRecommendationBottomSheet = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const options = [
    {
      id: 'Details',
      renderNavItem: ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
        <SidePanelButton
          selected={selected}
          onClick={onClick}
          Icon={ProposedPayroll}
          size={SIDE_PANEL_BUTTON_SIZE.SMALL}
          startIconProps={{}}
          notificationCount={0}
        >
          Proposed payroll
        </SidePanelButton>
      ),
      SectionContent: DetailsSection,
    },
    {
      id: 'Overview',
      renderNavItem: ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
        <SidePanelButton
          selected={selected}
          onClick={onClick}
          Icon={Details2}
          size={SIDE_PANEL_BUTTON_SIZE.SMALL}
          startIconProps={{}}
          notificationCount={0}
        >
          Overview
        </SidePanelButton>
      ),
      SectionContent: OverviewSection,
    },
    {
      id: 'History',
      renderNavItem: ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
        <SidePanelButton
          selected={selected}
          onClick={onClick}
          Icon={PerformanceHistory}
          size={SIDE_PANEL_BUTTON_SIZE.SMALL}
          startIconProps={{}}
          notificationCount={0}
        >
          History
        </SidePanelButton>
      ),
      SectionContent: HistorySection,
    },
    {
      id: 'EmployeeDetails',
      renderNavItem: ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
        <SidePanelButton
          selected={selected}
          onClick={onClick}
          Icon={EmployeeDetailsIcon}
          size={SIDE_PANEL_BUTTON_SIZE.SMALL}
          startIconProps={{}}
          notificationCount={0}
        >
          Employee details
        </SidePanelButton>
      ),
      SectionContent: EmployeeDetailsSection,
    },
  ];

  const navContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '4px',
    overflowY: 'auto',
  };

  const sectionContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    borderRadius: '8px',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>{undefined}</DrawerHeader>
        <DrawerBody p={0} overflow="hidden">
          <Flex flexDir="column" h="full" p="4px">
            <Box flexShrink={0} mb="12px">
              <TextHeader4 bold p="8px 4px 0 4px">
                Compensation recommendations for Sarah Johnson
              </TextHeader4>
            </Box>

            <Grid flex="1" p="4px" bg="neutral.EBEFF3" borderRadius="8px" gap="4px" minH="0" templateColumns="64px 1fr">
              <ScrollableSectionNavV2
                options={options}
                navContainerStyles={navContainerStyles}
                sectionContainerStyles={sectionContainerStyles}
              />
            </Grid>
            <Flex
              p="16px"
              mt="8px"
              ml="-4px"
              width="calc(100% + 8px)"
              justifyContent="space-between"
              alignItems="center"
              bg="surface.F5F7F9"
              borderTop="1px solid"
              borderColor="border.CFD7DF"
              borderBottomRadius="8px"
            >
              <Button variant="transparentOutline" onClick={onClose}>
                Cancel
              </Button>
              <Button>Save</Button>
            </Flex>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </BottomDrawer>
  );
};
